import { useEffect, useRef, useState } from 'react';
import { _T, C } from '../../shared/theme';

function ArcGauge({ value, label, color, size = 110 }) {
  const toRad = a => a * Math.PI / 180;
  const cx = size / 2, cy = size / 2, r = size * 0.38;
  const startAngle = -215, arc = 250;

  // Arc endpoints
  const pt  = a => ({ x: cx + r * Math.cos(toRad(a)), y: cy + r * Math.sin(toRad(a)) });
  const p0  = pt(startAngle);
  const p1  = pt(startAngle + arc);
  const lB  = 1; // large-arc for background (always > 180)

  // Approximate arc length for dash animation
  const totalLen = (arc / 360) * 2 * Math.PI * r;

  // Scroll-into-view: animate once when visible
  const wrapRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setInView(true); return; }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Clamp and compute filled arc end-point
  const pct    = Math.min(Math.max(value, 0), 100) / 100;
  const fillArc = arc * pct;
  const lF     = fillArc > 180 ? 1 : 0;
  const p2     = pt(startAngle + fillArc);

  const gid = 'arc' + label.replace(/\W/g, '') + size;

  // Dash offset: start fully hidden, animate to revealed
  const dashOffset = inView ? totalLen * (1 - pct) : totalLen;

  return (
    <div ref={wrapRef} style={{ textAlign: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.4" />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
        </defs>

        {/* Background track */}
        <path
          d={`M${p0.x},${p0.y} A${r},${r},0,${lB},1,${p1.x},${p1.y}`}
          fill="none"
          stroke={_T.bg4}
          strokeWidth={size * 0.08}
          strokeLinecap="round"
        />

        {/* Animated fill arc via stroke-dasharray */}
        {value > 0 && (
          <path
            d={`M${p0.x},${p0.y} A${r},${r},0,${lF},1,${p2.x},${p2.y}`}
            fill="none"
            stroke={`url(#${gid})`}
            strokeWidth={size * 0.08}
            strokeLinecap="round"
            strokeDasharray={totalLen}
            strokeDashoffset={dashOffset}
            style={{ transition: inView ? 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none' }}
          />
        )}

        {/* Centre value */}
        <text x={cx} y={cy - 2} textAnchor="middle" fill={C.white}
          fontSize={size * 0.18} fontWeight="700" fontFamily="Inter">
          {Math.round(value)}
        </text>
        <text x={cx} y={cy + size * 0.15} textAnchor="middle" fill={C.silver}
          fontSize={size * 0.09} fontFamily="Inter">
          {label}
        </text>
      </svg>
    </div>
  );
}

export default ArcGauge;
